import React from 'react';
import {
  Alert, Badge, Button, ErrorState, Table, TrendChart,
} from '@parsina/framework';
import { chartPalette } from '@parsina/framework/tokens';
import { Copy } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import {
  EMPTY, formatCreditPrice, formatUsd, isIrtCredit,
} from '../../lib/format';
import { num } from '../../lib/aiPricingView';
import { SERVICE_DETAIL_QUERY } from './queries';
import {
  BlockSkeleton, Code, CountCell, DetailDrawer, Fact, MarginBadge, ParsehCell, PercentCell,
  PriceSourceBadge, TomanCell, UsdCell, useEnumLabel, useServiceLabel,
} from './shared';

/**
 * One service, in the five sections of design §12.5:
 *   1. header facts — wallet type, charge mode, declared vs observed roles
 *   2. «سهم مدل‌ها»
 *   3. «مراحل» — where a five-call treatment plan shows which call is the dear one
 *   4. the daily trend
 *   5. «قیمت‌گذاری» — the policy's arithmetic for this row, with the disabled
 *      «ویرایش در سیاست فعال»: an active policy is immutable, so the only live
 *      button clones it into a new draft.
 */
export default function ServiceDrawer({ serviceKey, window, onClose, onClonePolicy }) {
  const { t, formatNumber, formatDate, formatMoney } = useTranslation();
  const label = useServiceLabel();
  const roleLabel = useEnumLabel('aiPricing.services.role');
  const chargeModeLabel = useEnumLabel('aiPricing.services.chargeMode');
  const basisLabel = useEnumLabel('aiPricing.services.drawer.basisSource');
  const usd = (v) => formatUsd(v, { formatNumber, t });
  const tokens = (v) => (num(v) == null ? EMPTY : formatNumber(num(v), { maximumFractionDigits: 0 }));

  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    gql(SERVICE_DETAIL_QUERY, { serviceKey, window })
      .then((d) => setDetail(d?.aiServiceDetail || null))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [serviceKey, window, t]);

  React.useEffect(() => { load(); }, [load]);

  const row = detail?.row;
  const pricing = detail?.pricing;
  const observedRoles = new Set((detail?.models || []).map((m) => m.role));
  const declared = detail?.declaredRoles || [];
  const rolesMismatch = row?.declaredRolesMismatch || declared.some((r) => !r.observed);
  const daily = detail?.daily || [];

  return (
    <DetailDrawer
      open
      onClose={onClose}
      title={row ? label(row) : t('aiPricing.services.columns.service')}
      sub={<Code>{serviceKey}</Code>}
      data-testid="ai-services-drawer"
    >
      {loading ? (
        <div data-state="loading" role="status" aria-busy="true" aria-label={t('aiPricing.services.drawer.loading')}>
          <BlockSkeleton lines={6} height={18} />
        </div>
      ) : error || !detail ? (
        <ErrorState
          title={t('aiPricing.services.drawer.error')}
          description={error || t('errors.region.noAnswer')}
          onRetry={load}
          retryLabel={t('common.actions.retry')}
          data-testid="ai-services-drawer-error"
        />
      ) : (
        <div className="flex flex-col gap-6" data-state="content">
          {/* 1 — header facts */}
          <section>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <Fact label={t('aiPricing.services.drawer.walletType')} value={row.walletServiceType} ltr />
              <Fact
                label={t('aiPricing.services.drawer.chargeMode')}
                value={(
                  <span className="inline-flex flex-wrap gap-1">
                    <Badge variant="outline">{chargeModeLabel(row.chargeMode)}</Badge>
                    {row.chargeCondition ? <Badge variant="outline">{t(`aiPricing.services.chargeCondition.${row.chargeCondition}`)}</Badge> : null}
                  </span>
                )}
              />
              <Fact label={t('aiPricing.services.columns.invocations')} value={<CountCell value={row.invocations} />} />
              <Fact label={t('aiPricing.services.drawer.columns.callsPer')} value={<CountCell value={row.avgCalls} options={{ maximumFractionDigits: 1 }} />} />
            </dl>
            <div className="mt-4">
              <h3 className="mb-1 text-xs text-muted-foreground">{t('aiPricing.services.drawer.roles')}</h3>
              <div className="flex flex-wrap gap-1.5" data-testid="ai-services-drawer-roles">
                {declared.map((r) => (
                  <Badge
                    key={r.role}
                    variant={r.observed ? 'success' : 'warning'}
                    title={r.expectedCalls != null
                      ? t('aiPricing.services.drawer.expectedCalls', { expected: r.expectedCalls, observed: tokens(row.avgCalls) })
                      : undefined}
                  >
                    {roleLabel(r.role)}
                    {r.observed ? null : <span className="ms-1 opacity-80">({t('aiPricing.services.drawer.roleNotObserved')})</span>}
                  </Badge>
                ))}
                {[...observedRoles].filter((role) => !declared.some((d) => d.role === role)).map((role) => (
                  <Badge key={`obs-${role}`} variant="outline" title={t('aiPricing.services.drawer.rolesObserved')}>{roleLabel(role)}</Badge>
                ))}
              </div>
              {rolesMismatch ? (
                <Alert variant="warning" data-testid="ai-services-drawer-roles-mismatch">
                  {t('aiPricing.services.drawer.rolesMismatch')}
                </Alert>
              ) : null}
            </div>
          </section>

          {/* 2 — model share */}
          <section>
            <h3 className="mb-2 text-sm font-bold text-foreground">{t('aiPricing.services.drawer.modelsTitle')}</h3>
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table
                dense
                ariaLabel={t('aiPricing.services.drawer.modelsAria')}
                rows={detail.models || []}
                rowKey={(m) => `${m.modelId}:${m.role}`}
                columns={[
                  { key: 'model', header: t('aiPricing.services.drawer.columns.model'), render: (m) => <Code title={m.modelId}>{m.modelId}</Code> },
                  { key: 'role', header: t('aiPricing.services.drawer.columns.role'), render: (m) => roleLabel(m.role) },
                  { key: 'calls', header: t('aiPricing.services.drawer.columns.calls'), align: 'end', render: (m) => <CountCell value={m.calls} /> },
                  { key: 'avgIn', header: t('aiPricing.services.drawer.columns.avgIn'), align: 'end', render: (m) => tokens(m.avgInputTokens) },
                  { key: 'avgOut', header: t('aiPricing.services.drawer.columns.avgOut'), align: 'end', render: (m) => tokens(m.avgOutputTokens) },
                  { key: 'cost', header: t('aiPricing.services.drawer.columns.cost'), align: 'end', render: (m) => (m.unpriced ? <Badge variant="destructive">{t('aiPricing.models.status.unknown')}</Badge> : <UsdCell value={m.costUsd} />) },
                  { key: 'share', header: t('aiPricing.services.drawer.columns.share'), align: 'end', render: (m) => <PercentCell value={m.shareOfCostPct} /> },
                ]}
                empty={<p className="p-4 text-sm text-muted-foreground">{t('aiPricing.models.drawer.servicesEmpty')}</p>}
                data-testid="ai-services-drawer-models"
              />
            </div>
          </section>

          {/* 3 — steps */}
          <section>
            <h3 className="mb-2 text-sm font-bold text-foreground">{t('aiPricing.services.drawer.stepsTitle')}</h3>
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table
                dense
                ariaLabel={t('aiPricing.services.drawer.stepsAria')}
                rows={detail.steps || []}
                rowKey={(s) => s.step}
                columns={[
                  { key: 'step', header: t('aiPricing.services.drawer.columns.step'), render: (s) => <Code>{s.step}</Code> },
                  { key: 'callsPer', header: t('aiPricing.services.drawer.columns.callsPer'), align: 'end', render: (s) => <CountCell value={s.callsPerInvocation} options={{ maximumFractionDigits: 2 }} /> },
                  { key: 'avgIn', header: t('aiPricing.services.drawer.columns.avgIn'), align: 'end', render: (s) => tokens(s.avgInputTokens) },
                  { key: 'avgOut', header: t('aiPricing.services.drawer.columns.avgOut'), align: 'end', render: (s) => tokens(s.avgOutputTokens) },
                  { key: 'cost', header: t('aiPricing.services.drawer.columns.cost'), align: 'end', render: (s) => <UsdCell value={s.costUsd} /> },
                  { key: 'share', header: t('aiPricing.services.drawer.columns.share'), align: 'end', render: (s) => <PercentCell value={s.shareOfCostPct} /> },
                ]}
                empty={<p className="p-4 text-sm text-muted-foreground">{t('aiPricing.overview.breakdownEmpty')}</p>}
                data-testid="ai-services-drawer-steps"
              />
            </div>
          </section>

          {/* 4 — trend */}
          <section data-testid="ai-services-drawer-trend">
            <h3 className="mb-2 text-sm font-bold text-foreground">{t('aiPricing.services.drawer.trendTitle')}</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <div className="mb-1 text-xs text-muted-foreground">{t('aiPricing.services.drawer.trendCost')}</div>
                <TrendChart
                  readings={daily.map((d) => ({ id: d.day, value: d.costUsd, day: d.day }))}
                  color={chartPalette[2]}
                  height={130}
                  formatX={(r) => formatDate(r.day, 'dayMonth')}
                  formatValue={(v) => formatNumber(v, { maximumFractionDigits: 2 })}
                  emptyLabel={t('aiPricing.overview.chart.empty')}
                />
              </div>
              <div>
                <div className="mb-1 text-xs text-muted-foreground">{t('aiPricing.services.drawer.trendInvocations')}</div>
                <TrendChart
                  readings={daily.map((d) => ({ id: d.day, value: d.invocations, day: d.day }))}
                  color={chartPalette[0]}
                  height={130}
                  formatX={(r) => formatDate(r.day, 'dayMonth')}
                  formatValue={(v) => formatNumber(v, { maximumFractionDigits: 0 })}
                  emptyLabel={t('aiPricing.overview.chart.empty')}
                />
              </div>
            </div>
            {row.unit === 'minute' ? (
              <p className="mt-2 text-xs text-muted-foreground" title={t('aiPricing.services.drawer.minutesRatio')}>
                {t('aiPricing.services.drawer.minutesRatioValue', {
                  sent: num(detail.audioMinutesSent) == null ? EMPTY : formatNumber(num(detail.audioMinutesSent), { maximumFractionDigits: 1 }),
                  billed: num(detail.billedMinutes) == null ? EMPTY : formatNumber(num(detail.billedMinutes), { maximumFractionDigits: 1 }),
                })}
              </p>
            ) : null}
          </section>

          {/* 5 — pricing */}
          <section className="rounded-lg border border-border bg-muted/40 p-4" data-testid="ai-services-drawer-pricing">
            <h3 className="mb-3 text-sm font-bold text-foreground">{t('aiPricing.services.drawer.pricingTitle')}</h3>
            <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
              <Fact
                label={t('aiPricing.services.drawer.basis')}
                value={(
                  <span className="inline-flex flex-wrap items-center gap-1">
                    <UsdCell value={pricing.basisCostUsd} /> {t('common.units.usd')}
                    <Badge variant={pricing.basisSource === 'none' ? 'destructive' : pricing.basisSource === 'declared' ? 'warning' : 'outline'}>
                      {basisLabel(pricing.basisSource)}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{t('aiPricing.services.drawer.basisSample', { count: pricing.basisSample || 0 })}</span>
                  </span>
                )}
              />
              {/* Toman and its USD rate only for a credit priced in toman; any
                  other currency prices a credit with no FX (PM-0211). */}
              {isIrtCredit(pricing.creditCurrency) ? (
                <>
                  <Fact label={t('aiPricing.services.drawer.fx')} value={num(pricing.fxTomanPerUsd) == null ? EMPTY : <span><TomanCell value={pricing.fxTomanPerUsd} /> {t('common.units.toman')}</span>} />
                  <Fact label={t('aiPricing.services.drawer.parseh')} value={num(pricing.parsehToman) == null ? EMPTY : <span><TomanCell value={pricing.parsehToman} /> {t('common.units.toman')}</span>} />
                </>
              ) : (
                <Fact
                  label={t('aiPricing.services.drawer.parseh')}
                  value={formatCreditPrice(pricing.creditPrice, pricing.creditCurrency, { t, formatNumber, formatMoney })}
                />
              )}
              <Fact label={t('aiPricing.services.drawer.computed')} value={<ParsehCell value={pricing.computedParseh} />} />
              <Fact label={t('aiPricing.services.drawer.override')} value={<ParsehCell value={pricing.overrideParseh} />} />
              <Fact
                label={t('aiPricing.services.drawer.effective')}
                value={(
                  <span className="inline-flex items-center gap-1">
                    <ParsehCell value={pricing.effectiveParseh} suffix={row.unit === 'minute' ? t('aiPricing.services.perMinute') : ''} className="font-semibold" />
                    <PriceSourceBadge source={row.priceSource} />
                  </span>
                )}
              />
              <Fact
                label={t('aiPricing.services.drawer.margin')}
                value={num(row.chargedRate) === 0
                  ? <span className="text-muted-foreground">{t('aiPricing.services.drawer.neverCharged')}</span>
                  : <MarginBadge value={pricing.marginPct} />}
              />
            </dl>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {pricing.policyId ? (
                <>
                  {/* Disabled on purpose, with the reason: the active policy is
                      immutable (design §8.4). The live button clones it. */}
                  <Button variant="outline" disabled title={t('aiPricing.services.drawer.editActiveHint')} data-testid="ai-services-drawer-edit-active">
                    {t('aiPricing.services.drawer.editActive')}
                  </Button>
                  <Button onClick={() => onClonePolicy(pricing.policyId)} data-testid="ai-services-drawer-clone">
                    <Copy size={16} aria-hidden />
                    {t('aiPricing.services.drawer.clone')}
                  </Button>
                  <span className="text-xs text-muted-foreground">{pricing.policyName}</span>
                </>
              ) : (
                <>
                  <span className="text-xs text-muted-foreground">{t('aiPricing.services.drawer.noPolicy')}</span>
                  <Button onClick={() => onClonePolicy(null)} data-testid="ai-services-drawer-clone">
                    <Copy size={16} aria-hidden />
                    {t('aiPricing.services.drawer.clone')}
                  </Button>
                </>
              )}
            </div>
          </section>
        </div>
      )}
    </DetailDrawer>
  );
}
